import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Transaction } from '../../../models/transaction.interface';
import { Subscription } from 'rxjs';
import { TableColumn } from '../../../../../shared/models';
import {
  loadTransactions,
  reloadTransactions,
  resetTransactionHistoryState,
} from '../../../store/actions/transaction-history.actions';
import {
  selectTransactions,
  selectTransactionsFilter,
  selectTransactionsLoading,
} from '../../../store/selectors/transaction-history.selectors';
import { TransactionStatus, TransactionStatusColor } from '../../../enums/transaction-status.enum';
import { MatDialog } from '@angular/material/dialog';
import { TransactionDetailsModalComponent } from '../transaction-details-modal/transaction-details-modal.component';
import { Store } from '@ngrx/store';
import { PaypointService } from '../../../services/paypoint.service';
import { LayoutUtility } from '../../../../../shared/utils';
import { VoidModalComponent } from '../void-modal/void-modal.component';
import { ContactsService } from '../../../../lists';

@Component({
  selector: 'kt-bridgepay-transaction-table',
  templateUrl: './transaction-table.component.html',
  styleUrls: ['./transaction-table.component.scss'],
})
export class TransactionTableComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [];
  @ViewChild('paymentCode', { static: true }) paymentCode: TemplateRef<any>;
  @ViewChild('contact', { static: true }) contact: TemplateRef<any>;
  @ViewChild('dateAndTime', { static: true }) dateAndTime: TemplateRef<any>;
  @ViewChild('statusRef', { static: true }) statusRef: TemplateRef<any>;
  @ViewChild('actionsRef', { static: true }) actionsRef: TemplateRef<any>;
  @ViewChild('paymentCodeFilter', { static: true }) paymentCodeFilter: TemplateRef<any>;
  @ViewChild('dateTimeFilter', { static: true }) dateTimeFilter: TemplateRef<any>;
  @ViewChild('amountFilter', { static: true }) amountFilter: TemplateRef<any>;
  @ViewChild('statusFilter', { static: true }) statusFilter: TemplateRef<any>;
  @ViewChild('contactFilter', { static: true }) contactFilter: TemplateRef<any>;
  statusFilterOptions = Object.values(TransactionStatus);
  loadTransactionsAction = loadTransactions;
  resetTransactionsStateAction = resetTransactionHistoryState;
  transactionsSelector = selectTransactions;
  transactionsFilterSelector = selectTransactionsFilter;
  transactionsLoadingSelector = selectTransactionsLoading;
  subscriptions: Subscription[] = [];
  contact_UUIDs: string[] = [];
  contacts = [];
  contactOptions = [];

  constructor(
    private payPointService: PaypointService,
    private contactsService: ContactsService,
    private dialog: MatDialog,
    private store: Store,
    private layoutUtility: LayoutUtility,
  ) {}

  ngOnInit() {
    this.columns = [
      {
        columnDef: 'contact',
        header: 'Contact',
        cell: () => '',
        template: this.contact,
        sortable: false,
        filterable: true,
        filterTemplate: this.contactFilter,
        filterDefaultValue: '',
        filterQueryParam: 'contact',
      },
      {
        columnDef: 'paymentCode',
        header: 'Payment Code',
        cell: () => '',
        template: this.paymentCode,
        sortable: true,
        filterable: true,
        filterTemplate: this.paymentCodeFilter,
        filterDefaultValue: '',
        filterQueryParam: 'transactionCode',
      },
      {
        columnDef: 'dateTime',
        header: 'Date Time',
        cell: () => '',
        sortable: true,
        template: this.dateAndTime,
        filterable: true,
        filterTemplate: this.dateTimeFilter,
        filterDefaultValue: '',
        filterQueryParam: 'dateTime',
      },
      {
        columnDef: 'amount',
        header: 'Amount',
        cell: (transaction: Transaction) => `$` + `${transaction.amount}` + `.00`,
        sortable: true,
        filterable: true,
        filterTemplate: this.amountFilter,
        filterDefaultValue: '',
        filterQueryParam: 'amount',
      },
      {
        columnDef: 'status',
        header: 'Status',
        cell: () => '',
        template: this.statusRef,
        sortable: true,
        filterable: true,
        filterTemplate: this.statusFilter,
        filterDefaultValue: '',
        filterQueryParam: 'status',
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        cell: () => '',
        template: this.actionsRef,
      },
    ];
    this.store.select(this.transactionsSelector).subscribe((s) => {
      s.items.forEach((item) => {
        this.contact_UUIDs.push(item.customer_id);
      });
      this.payPointService.getContactsByUUIDs(this.contact_UUIDs).subscribe((res) => {
        this.payPointService.initiateContactMappingList(res.data);
        this.contactOptions = res.data;
      });
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onViewDetails(transaction: Transaction): void {
    const transactionDialog = this.dialog.open(TransactionDetailsModalComponent, {
      data: { transaction },
      width: '650px',
    });
    transactionDialog.afterClosed().subscribe((res) => {
      if (res?.voided) {
        this.store.dispatch(reloadTransactions());
        return this.layoutUtility.showActionNotification(
          'SUCCESS',
          'Transaction successfully voided',
        );
      }
    });
  }

  getTransactionStatusColor(status: TransactionStatus): 'tertiary' | 'danger' | 'success' {
    return TransactionStatusColor[status];
  }

  getContactName(uuid: string) {
    return this.payPointService.mappedContactFromListGetName(uuid);
  }

  getContactID(uuid: string) {
    return this.payPointService.mappedContactFromListGetID(uuid);
  }

  voidTransaction(item: Transaction) {
    const voidConfirmingDialog = this.dialog.open(VoidModalComponent, {
      width: '500px',
      data: { transaction: item },
    });
    voidConfirmingDialog.afterClosed().subscribe((result) => {
      if (result?.voided) {
        this.store.dispatch(reloadTransactions());
        return this.layoutUtility.showActionNotification(
          'SUCCESS',
          'Transaction successfully voided',
        );
      }
      return this.layoutUtility.showActionNotification('ERROR', 'Voiding transaction failed');
    });
  }
  formatContact(contact): string {
    return contact?.first_name + ' ' + contact?.last_name || '';
  }
}
